import { Badge, rem, Tooltip, useComputedColorScheme } from "@mantine/core";
import {
  IconBroadcast,
  IconBroadcastOff,
  IconFocusCentered,
  IconPlayerPlay,
  IconSchool,
} from "@tabler/icons-react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  selectScene,
  selectProductId,
  selectSelectedDeviceStatus,
} from "@utils/redux-utils/selectors/productSelectors";
import { selectIsDialogOpen } from "@utils/redux-utils/selectors/dialogSelector";
import { useScenePolling } from "../../../../hooks/useScenePolling";
import { DEVICE_STATUS } from "./DeviceStatus";

class DeviceBadgeProps {
  dontPoll?: boolean;
  deviceStatus?: number | null;
  tooltip?: boolean;
}

function DeviceStatusBadge(props: DeviceBadgeProps) {
  const { t } = useTranslation();
  const colorScheme = useComputedColorScheme("light");

  const selectedProductId = useSelector(selectProductId);
  const isDialogOpen = useSelector(selectIsDialogOpen);
  const latestScene = useSelector(selectScene);
  const selectedDeviceStatus = useSelector(selectSelectedDeviceStatus);
  const deviceStatus = props.deviceStatus ?? selectedDeviceStatus;

  useScenePolling(selectedProductId, {
    enabled: !props.dontPoll && !isDialogOpen,
  });

  const isCalibrating = deviceStatus === DEVICE_STATUS.Calibration;
  const isInTutorial = deviceStatus === DEVICE_STATUS.Tutorial;
  const isTesting = deviceStatus === DEVICE_STATUS.Test;
  const isConnected = deviceStatus === DEVICE_STATUS.Menu;

  const labelText = isCalibrating
    ? t("calibrationStatusBadge.calibrating")
    : isInTutorial
      ? t("Tutorial")
      : isTesting
        ? t("Test")
        : isConnected
          ? t("Connected")
          : t("NotConnected");

  const tooltipText =
    isTesting || isCalibrating
      ? latestScene && latestScene.length > 0
        ? latestScene
        : labelText
      : labelText;

  const { tooltip: tooltip = false, dontPoll, deviceStatus: _, ...badgeProps } = props;

  const iconSize = rem(12);

  const icon = (size: string, disconnectedColor?: string) => {
    const style = { width: size, height: size };
    if (isCalibrating) {
      return <IconFocusCentered style={style} />;
    }
    if (isInTutorial) {
      return <IconSchool style={style} />;
    }
    if (isTesting) {
      return <IconPlayerPlay style={style} />;
    }
    if (isConnected) {
      return <IconBroadcast style={style} />;
    }
    return <IconBroadcastOff color={disconnectedColor} style={style} />;
  };

  const badge = (
    <Badge
      {...badgeProps}
      variant={colorScheme === "dark" ? "light" : "filled"}
      color={
        isCalibrating
          ? "violet"
          : isInTutorial
            ? "grape"
            : isTesting
              ? "orange"
              : isConnected
                ? "blue"
                : "gray"
      }
      gradient={{ from: "teal", to: "blue", deg: 220 }}
      size={tooltip ? "md" : "lg"}
      radius="xl"
      leftSection={tooltip ? undefined : icon(iconSize, "white")}
      styles={
        tooltip
          ? { label: { display: "flex", alignItems: "center", justifyContent: "center" } }
          : undefined
      }
    >
      {tooltip ? icon(rem(14)) : labelText}
    </Badge>
  );

  if (tooltip) {
    return (
      <Tooltip withArrow label={tooltipText}>
        {badge}
      </Tooltip>
    );
  }

  return badge;
}

export default DeviceStatusBadge;
